import { useRef, useState } from "react";

interface DeleteConfirmDialogProps {
  isZh: boolean;
  count: number;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

export default function DeleteConfirmDialog({
  isZh,
  count,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const deletingRef = useRef(false);

  const handleConfirm = async () => {
    if (deletingRef.current) return;
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      onClick={isDeleting ? undefined : onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-busy={isDeleting}
        className="bg-white rounded-2xl w-full max-w-sm mx-4 flex flex-col overflow-hidden"
        style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.16)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
            style={{ backgroundColor: "rgba(239,68,68,0.1)" }}
          >
            <i className="ri-delete-bin-line text-base" style={{ color: "#ef4444" }}></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">
            {isZh ? "确认删除文件" : "Confirm file deletion"}
          </h3>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            {isZh
              ? `即将删除 ${count} 个文件。此操作不可撤销，文件将从您的个人知识库中移除。`
              : `You are about to delete ${count} file${count > 1 ? "s" : ""}. This action cannot be undone and files will be removed from your personal knowledge base.`}
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={isDeleting}
            onClick={onCancel}
            className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          >
            {isZh ? "取消" : "Cancel"}
          </button>
          <button
            type="button"
            disabled={isDeleting}
            onClick={() => void handleConfirm()}
            className="inline-flex min-w-[108px] items-center justify-center gap-1.5 text-xs px-4 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
            style={{ backgroundColor: "#ef4444" }}
          >
            {isDeleting ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                {isZh ? "删除中…" : "Deleting…"}
              </>
            ) : isZh ? (
              `删除 ${count} 个文件`
            ) : (
              `Delete ${count} file${count > 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
